import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FaIcons from 'react-icons/fa';

const { FaGoogle, FaYelp, FaHeart, FaRing } = FaIcons;

const platforms = [
  {
    id: 'google',
    name: 'Google',
    icon: FaGoogle,
    description: 'Best for helping future couples discover Padua on Google and Maps.',
    buttonText: 'Leave a Google Review',
    url: 'https://www.google.com/search?q=Padua+Weddings+Google+Review',
    featured: true,
    note: 'If the review window does not open directly, search Padua Weddings on Google and click ‘Write a Review.’'
  },
  {
    id: 'theknot',
    name: 'The Knot',
    icon: FaRing,
    description: 'Helpful for engaged couples comparing wedding venues.',
    buttonText: 'Review Us on The Knot',
    url: 'https://www.theknot.com/review-wedding-vendors/256708',
    featured: false,
  },
  {
    id: 'weddingwire',
    name: 'WeddingWire',
    icon: FaHeart,
    description: 'Share your experience with couples planning their wedding.',
    buttonText: 'Review Us on WeddingWire',
    url: 'https://www.weddingwire.com/shared/rate/new?vid=a738f8600fc3fa2c',
    featured: false,
  },
  {
    id: 'yelp',
    name: 'Yelp',
    icon: FaYelp,
    description: 'Helpful for local discovery and community recommendations.',
    buttonText: 'Leave a Yelp Review',
    url: 'https://www.yelp.com/writeareview/biz/1jsGSYu-3AreUyJHT9fzvQ?return_url=%2Fbiz%2F1jsGSYu-3AreUyJHT9fzvQ&review_origin=biz-details-war-button',
    featured: false,
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const PlatformCards = () => {
  return (
    <section id="platforms" className="w-full">
      <div className="text-center mb-10">
        <h3 className="font-serif text-3xl md:text-4xl text-padua-dark mb-4">Choose Where to Share Your Review</h3>
        <p className="text-padua-sage font-medium max-w-lg mx-auto leading-relaxed">
          Google is the most helpful for future couples, but you can choose whichever platform is easiest for you.
        </p>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="flex flex-col gap-5 w-full max-w-2xl mx-auto"
      >
        {platforms.map((platform) => (
          <motion.div key={platform.id} variants={itemVariants}>
            <motion.a
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.98 }}
              className={`group block w-full rounded-2xl p-6 md:p-8 transition-all duration-300 relative overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(74,92,64,0.12)] ${
                platform.featured 
                  ? 'border-2 border-padua-gold/50 shadow-[0_8px_30px_rgb(197,160,89,0.15)]' 
                  : 'border border-padua-sage/20 hover:border-padua-olive/30'
              }`}
            >
              {platform.featured && (
                <div className="absolute top-0 right-0 bg-padua-gold text-white text-[10px] md:text-xs font-bold uppercase tracking-wider py-1 px-3 md:px-4 rounded-bl-xl font-sans">
                  Most Recommended
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`mt-1 p-3 rounded-full flex-shrink-0 transition-colors duration-300 ${
                    platform.featured 
                      ? 'bg-padua-gold/10 text-padua-gold' 
                      : 'bg-padua-lightSage text-padua-olive group-hover:bg-padua-olive group-hover:text-white'
                  }`}>
                    <SafeIcon icon={platform.icon} className="w-6 h-6 md:w-7 md:h-7" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-padua-dark mb-1 group-hover:text-padua-olive transition-colors">
                      {platform.name}
                    </h3>
                    <p className="text-sm md:text-base text-gray-500 font-light leading-relaxed">
                      {platform.description}
                    </p>
                  </div>
                </div>
                <div className="sm:w-auto w-full flex-shrink-0">
                  <span className={`inline-flex w-full justify-center items-center px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                    platform.featured 
                      ? 'bg-padua-olive text-white shadow-md hover:bg-[#3d4d35]' 
                      : 'bg-white border border-padua-olive/20 text-padua-olive group-hover:bg-padua-olive group-hover:text-white group-hover:border-padua-olive'
                  }`}>
                    {platform.buttonText}
                  </span>
                </div>
              </div>
            </motion.a>
            {platform.note && (
              <p className="mt-3 text-xs text-center text-padua-sage font-medium px-4">
                {platform.note}
              </p>
            )}
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

export default PlatformCards;